/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import type { SearchResultItem } from '../../../api/search.types';
import { getTargetUri } from '../../../utils/pathUtils';
import {
    getCardRating,
    getPriceRange,
    useOnSearchResultCardLinkClick,
    formatLocationName,
} from '../SearchResultCard.utils';
import { CardSurfaceContext } from '../CardSurfaceContext';

interface SearchResultCardLinkHeadingProps {
    item: SearchResultItem;
    id?: string;
    className?: string;
}

export function SearchResultCardLinkHeading({
    item,
    id,
    className,
}: SearchResultCardLinkHeadingProps) {
    const targetUri = getTargetUri(item);
    const onSearchResultCardLinkClick = useOnSearchResultCardLinkClick();
    const { ratingAriaLabelShort } = getCardRating(item);
    const { priceRangeAriaLabel } = getPriceRange(item);
    const surface = React.useContext(CardSurfaceContext);

    const formattedLocationName = formatLocationName(item.name);
    const ariaLabel = `${formattedLocationName} ${priceRangeAriaLabel ? `/ ${priceRangeAriaLabel}` : ''} ${ratingAriaLabelShort ? `/ ${ratingAriaLabelShort}` : ''} (opens in a new tab)`;

    return (
        <h2 className={cx('card-heading', className)} id={id}>
            <a
                className="card-link"
                href={targetUri}
                target="_blank"
                rel="noopener noreferrer"
                data-ga-tracking-id="090142933987"
                data-ga-tracking-value={formatLocationName(item.name)}
                data-ga-tracking-location={surface ?? undefined}
                onClick={(event) => {
                    onSearchResultCardLinkClick(event, item);
                }}
                aria-label={ariaLabel}
            >
                {formattedLocationName}
            </a>
        </h2>
    );
}
